import { Group, Text, UnstyledButton } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { use, useState, useTransition } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { LinkDTO } from "@shortner/shared";

import { requireSignedIn } from "@features/auth/route-guards";
import { CreateLinkModal } from "@features/links/CreateLinkModal";
import { HeroSummary } from "@features/links/HeroSummary";
import { LinkDetail } from "@features/links/LinkDetail";
import { LinkTable } from "@features/links/LinkTable";
import { useLinks } from "@features/links/use-links";
import { WorkspaceContext } from "@features/workspace/WorkspaceContext";
import { PrimaryButton } from "@shared/components/buttons/PrimaryButton";
import { LoadingShell } from "@shared/components/common/LoadingShell";
import { QuotaBanner } from "@shared/components/common/QuotaBanner";
import { AppShell } from "@shared/components/layout/AppShell";

const PAGE_SIZE = 50;

export const Route = createFileRoute("/")({
  beforeLoad: requireSignedIn,
  component: Dashboard,
});

function Dashboard() {
  const workspace = use(WorkspaceContext);
  const intl = useIntl();
  const [page, setPage] = useState(0);
  const [, startTransition] = useTransition();
  const [createOpen, setCreateOpen] = useState(false);
  const [activeLink, setActiveLink] = useState<LinkDTO | null>(null);
  const [quotaMessage, setQuotaMessage] = useState<string | null>(null);
  const { data, isLoading } = useLinks({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  if (!workspace) return null;

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalClicks = items.reduce((sum, link) => sum + link.clickCount, 0);
  const activeLinks = items.filter((link) => link.isActive).length;
  const planName = intl.formatMessage({
    id: "dashboard.summary.planFree",
    defaultMessage: "Free",
  });

  function openCreate() {
    setQuotaMessage(null);
    setCreateOpen(true);
  }

  return (
    <AppShell.Root>
      <AppShell.TopBar />
      <AppShell.Content>
        <HeroSummary activeLinks={activeLinks} totalClicks={totalClicks} planName={planName} />
        <Group justify="flex-end" my={32}>
          <PrimaryButton onClick={openCreate}>
            <FormattedMessage id="dashboard.newLink" defaultMessage="New link" />
          </PrimaryButton>
        </Group>
        {quotaMessage ? <QuotaBanner message={quotaMessage} /> : null}
        {isLoading ? <LoadingShell /> : <LinkTable links={items} onOpen={setActiveLink} />}
        {total > PAGE_SIZE ? (
          <Group justify="center" mt={32} gap={24}>
            <UnstyledButton
              onClick={() => startTransition(() => setPage((p) => Math.max(0, p - 1)))}
              disabled={page === 0}
              style={{
                color: page === 0 ? "var(--ink-muted)" : "var(--ink)",
                fontSize: 14,
                opacity: page === 0 ? 0.5 : 1,
              }}
            >
              <FormattedMessage id="dashboard.pagination.previous" defaultMessage="Previous" />
            </UnstyledButton>
            <Text size="sm" c="var(--ink-muted)" style={{ fontVariantNumeric: "tabular-nums" }}>
              <FormattedMessage
                id="dashboard.pagination.position"
                defaultMessage="{current} of {total}"
                values={{
                  current: page + 1,
                  total: Math.ceil(total / PAGE_SIZE),
                }}
              />
            </Text>
            <UnstyledButton
              onClick={() => startTransition(() => setPage((p) => p + 1))}
              disabled={(page + 1) * PAGE_SIZE >= total}
              style={{
                color: (page + 1) * PAGE_SIZE >= total ? "var(--ink-muted)" : "var(--ink)",
                fontSize: 14,
                opacity: (page + 1) * PAGE_SIZE >= total ? 0.5 : 1,
              }}
            >
              <FormattedMessage id="dashboard.pagination.next" defaultMessage="Next" />
            </UnstyledButton>
          </Group>
        ) : null}
      </AppShell.Content>
      <CreateLinkModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onQuotaExceeded={(message) => setQuotaMessage(message)}
      />
      <LinkDetail.Root link={activeLink} onClose={() => setActiveLink(null)}>
        <LinkDetail.Header />
        <LinkDetail.QR />
        <LinkDetail.EditForm />
        <LinkDetail.DangerZone />
      </LinkDetail.Root>
    </AppShell.Root>
  );
}
